/**
 * result.ts — Result<T, E> discriminated union
 *
 * Purpose:    Type-safe error handling without exceptions; used across all
 *             ɳSentry dashboard hooks to surface typed errors to AsyncScreen.
 * Inputs:     Generic type params T (success value) and E (error value)
 * Outputs:    Ok<T> | Err<E> with helpers ok() / err() / isOk() / isErr()
 * Constraints: Zero runtime deps; tree-shakes to ~200 bytes
 * SOT:        T-P3-E5-W2-S4-T01
 */

export type Ok<T> = { readonly ok: true; readonly value: T }
export type Err<E> = { readonly ok: false; readonly error: E }
export type Result<T, E> = Ok<T> | Err<E>

export function ok<T>(value: T): Ok<T> {
  return { ok: true, value }
}

export function err<E>(error: E): Err<E> {
  return { ok: false, error }
}

export function isOk<T, E>(result: Result<T, E>): result is Ok<T> {
  return result.ok
}

export function isErr<T, E>(result: Result<T, E>): result is Err<E> {
  return !result.ok
}
